import { useMemo } from 'react';
import type { ChangeEvent } from 'react';

import { Button, PasswordInput } from '@parrot-software/design-system';
import { LoaderCircle } from '@parrot-software/design-system/icons';
import { cn } from '@parrot-software/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form-v7';
import type { Resolver } from 'react-hook-form-v7';
import { useTranslation } from 'react-i18next';

import { IDENTITY_ERROR_CODES } from '@identity/application/errors';
import { isPasswordValid } from '@identity/domain/value-objects';
import { FieldLabel, PasswordRequirementsChecklist, StepCard } from '@identity/presentation/components/base';
import { useChangePassword } from '@identity/presentation/hooks';
import { resolveIdentityErrorMessage } from '@identity/presentation/utils';

import { createChangePasswordFormSchema } from './ChangePasswordView.schema';
import type { ChangePasswordFormValues } from './ChangePasswordView.schema';

/** The codes that belong under the field of the password being replaced, not under the button. */
const CURRENT_PASSWORD_ERROR_CODES: readonly string[] = [
  IDENTITY_ERROR_CODES.CURRENT_PASSWORD_INVALID,
  IDENTITY_ERROR_CODES.CURRENT_PASSWORD_REQUIRED,
];

/** The code that belongs under the field of the new password. */
const NEW_PASSWORD_ERROR_CODE = IDENTITY_ERROR_CODES.PASSWORD_POLICY;

export type ChangePasswordViewProps = {
  onCancel: () => void;
  onChanged: () => void;
};

/**
 * The in-session password change: the one being replaced, and the one replacing it.
 *
 * It is not the password step with more props. That step is a wizard page with one field, an email
 * block, a step label, an icon and a fixed use case; turning it into this would take seven optional
 * props and five branches to share a form and a button, and every one of those branches would be a
 * new way to break the three flows that use it today. What is genuinely common — the card, the
 * checklist, the label, the input and the policy — is already shared.
 *
 * Each rejection is painted where it can be acted on: the wrong current password under its own
 * field, the policy under the new one, anything else under the button.
 *
 * @param {ChangePasswordViewProps} props - Component properties
 */
export const ChangePasswordView = ({ onCancel, onChanged }: ChangePasswordViewProps) => {
  const { t } = useTranslation('identity');
  const { mutate: changePassword, isLoading, error, reset: resetRequest } = useChangePassword();
  const resolver = useMemo(
    () => (yupResolver(createChangePasswordFormSchema()) as unknown) as Resolver<ChangePasswordFormValues>,
    [],
  );

  const {
    clearErrors,
    formState: { errors },
    handleSubmit,
    register,
    watch,
  } = useForm<ChangePasswordFormValues>({
    defaultValues: { currentPassword: '', password: '' },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver,
  });

  const currentPassword = watch('currentPassword');
  const password = watch('password');
  // The same checklist the user sees, so the button enables exactly when the last rule turns green.
  const isSubmitDisabled = isLoading || !currentPassword || !isPasswordValid(password);

  const requestErrorKey = error ? resolveIdentityErrorMessage(error).i18nKey : undefined;
  const requestErrorCode = requestErrorKey?.split('.').pop();
  const currentPasswordRequestError = CURRENT_PASSWORD_ERROR_CODES.includes(requestErrorCode ?? '')
    ? requestErrorKey
    : undefined;
  const newPasswordRequestError = requestErrorCode === NEW_PASSWORD_ERROR_CODE ? requestErrorKey : undefined;
  const genericRequestError =
    requestErrorKey && !currentPasswordRequestError && !newPasswordRequestError ? requestErrorKey : undefined;

  const { onChange: onCurrentFieldChange, ...currentPasswordField } = register('currentPassword');
  const { onChange: onNewFieldChange, ...passwordField } = register('password');

  /** Typing clears what the previous value was told, both by the resolver and by the last request. */
  const editing = (
    name: keyof ChangePasswordFormValues,
    forward: (event: ChangeEvent<HTMLInputElement>) => unknown,
  ) => (event: ChangeEvent<HTMLInputElement>) => {
    if (errors[name]) clearErrors(name);
    if (error) resetRequest();

    return forward(event);
  };

  const onSubmit = handleSubmit((values) =>
    changePassword({ currentPassword: values.currentPassword, password: values.password }, { onSuccess: onChanged }),
  );

  return (
    <StepCard description={t('profile.changePassword.subtitle')} title={t('profile.changePassword.title')}>
      <form className="flex flex-col gap-6 font-sans" noValidate onSubmit={onSubmit}>
        <div className="flex flex-col gap-1.5">
          <FieldLabel htmlFor="identity-current-password" isRequired>
            {t('profile.changePassword.currentLabel')}
          </FieldLabel>
          <PasswordInput
            {...currentPasswordField}
            aria-invalid={Boolean(errors.currentPassword || currentPasswordRequestError)}
            aria-required
            autoComplete="current-password"
            className={cn({
              'border-destructive focus-visible:ring-destructive': Boolean(
                errors.currentPassword || currentPasswordRequestError,
              ),
            })}
            id="identity-current-password"
            onChange={editing('currentPassword', onCurrentFieldChange)}
          />
          {errors.currentPassword?.message && (
            <p className="text-xs text-destructive">{t(errors.currentPassword.message)}</p>
          )}
          {currentPasswordRequestError && (
            <p className="text-xs text-destructive" data-testid="current-password-request-error">
              {t(currentPasswordRequestError)}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <FieldLabel htmlFor="identity-new-password" isRequired>
            {t('profile.changePassword.newLabel')}
          </FieldLabel>
          <PasswordInput
            {...passwordField}
            aria-invalid={Boolean(errors.password || newPasswordRequestError)}
            aria-required
            autoComplete="new-password"
            className={cn({
              'border-destructive focus-visible:ring-destructive': Boolean(errors.password || newPasswordRequestError),
            })}
            id="identity-new-password"
            onChange={editing('password', onNewFieldChange)}
          />
          {errors.password?.message && <p className="text-xs text-destructive">{t(errors.password.message)}</p>}
          {newPasswordRequestError && (
            <p className="text-xs text-destructive" data-testid="new-password-request-error">
              {t(newPasswordRequestError)}
            </p>
          )}
        </div>

        <PasswordRequirementsChecklist value={password} />

        {genericRequestError && (
          <p className="text-xs text-destructive" data-testid="change-password-request-error">
            {t(genericRequestError)}
          </p>
        )}

        <div className="flex flex-col gap-3 sm:flex-row sm:justify-start">
          <Button onClick={onCancel} type="button" variant="outline">
            {t('profile.changePassword.cancel')}
          </Button>
          <Button disabled={isSubmitDisabled} type="submit">
            {isLoading && (
              <LoaderCircle
                aria-hidden
                className="size-4 shrink-0 animate-spin"
                data-testid="change-password-submit-spinner"
              />
            )}
            {t('profile.changePassword.submit')}
          </Button>
        </div>
      </form>
    </StepCard>
  );
};
